import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, computed, inject, signal, viewChild, WritableSignal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DEFAULT_MAX_RESULT_COUNT,
  DEFAULT_TOASTER_LIFE,
  IWizardContext,
  PERIOD_TYPE_NAME_SILVICULTURE,
  REASON_GROUP_NAME_AREA_ADJUSTMENT,
  SelectOption,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnPill,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import { firstValueFrom, tap } from 'rxjs';

import { StandCatalogs } from '../../stand/stand-creation-form/services/stand-catalogs';
import type {
  IAreaMaintenanceFormValue,
  IAreaMaintenanceSelectOption,
} from '../interfaces/IAreaMaintenanceFormValue';
import type { IResultingStandRow } from '../interfaces/IResultingStandRow';
import type { IStepCreateAreaMaintenancesData } from '../interfaces/IStepCreateAreaMaintenancesData';
import type { IStepDefineResultingStandsData } from '../interfaces/IStepDefineResultingStandsData';
import type { IStepSelectStandToDivideData } from '../interfaces/IStepSelectStandToDivideData';
import { StandPartitionPlanningService } from '../services/stand-partition-planning.service';

@Component({
  selector: 'lib-step-create-area-maintenances',
  standalone: true,
  imports: [LocalizationPipe, ReactiveFormsModule, ZnSingleSelect, ZnPill],
  templateUrl: './step-create-area-maintenances.html',
})
export class StepCreateAreaMaintenances
  implements WizardStepComponent<IStepCreateAreaMaintenancesData>
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly toaster = inject(ToasterService);
  private readonly standCatalogs = inject(StandCatalogs);
  private readonly standPartitionPlanningService = inject(StandPartitionPlanningService);
  readonly originalPeriodSelect = viewChild('originalPeriodSelect', { read: ZnSingleSelect });
  readonly originalReasonSelect = viewChild('originalReasonSelect', { read: ZnSingleSelect });
  readonly originalAdjustmentTypeSelect = viewChild('originalAdjustmentTypeSelect', {
    read: ZnSingleSelect,
  });
  readonly newPeriodSelect = viewChild('newPeriodSelect', { read: ZnSingleSelect });
  readonly newReasonSelect = viewChild('newReasonSelect', { read: ZnSingleSelect });
  readonly newAdjustmentTypeSelect = viewChild('newAdjustmentTypeSelect', {
    read: ZnSingleSelect,
  });

  readonly localizationKeyPrefix = 'Geographic::StandDivision.StepCreateAreaMaintenances';
  readonly localizationResource = ZendraLocalizationServices.LanguageService;
  readonly previousStepId = 2;
  readonly firstStepId = 1;

  readonly originalStandId = signal<string | null>(null);
  readonly originalStandName = signal('');
  readonly originalCurrentAreaValue = signal<number | null>(null);
  readonly originalAdjustedArea = signal<number | null>(null);
  readonly newStandRows = signal<IResultingStandRow[]>([]);

  readonly originalCurrentArea = computed(() => this.originalCurrentAreaValue());

  readonly originalAreaDelta = computed(() => {
    const currentArea = this.originalCurrentArea();
    const adjustedArea = this.originalAdjustedArea();

    if (currentArea === null || adjustedArea === null) {
      return null;
    }

    return this.roundArea(currentArea - adjustedArea);
  });

  readonly hasNewStands = computed(() => this.newStandRows().length > 0);
  readonly originalStandChip = computed(() => this.originalStandName() || '-----');
  readonly periodOptions = signal<SelectOption<string>[]>([]);
  readonly reasonOptions = signal<SelectOption<string>[]>([]);
  readonly adjustmentTypeOptions = signal<SelectOption<string>[]>([]);

  readonly originalForm = this.formBuilder.group({
    period: [null as string | null, [Validators.required]],
    reason: [null as string | null, [Validators.required]],
    adjustmentType: [null as string | null, [Validators.required]],
    details: [''],
    isAreaUpdated: [{ value: true, disabled: true }],
  });

  readonly newStandsForm = this.formBuilder.group({
    period: [null as string | null, [Validators.required]],
    reason: [null as string | null, [Validators.required]],
    adjustmentType: [null as string | null, [Validators.required]],
    details: [''],
    isAreaUpdated: [{ value: true, disabled: true }],
  });

  private readonly periodOptionsMap = new Map<string, SelectOption<string>>();
  private readonly reasonOptionsMap = new Map<string, SelectOption<string>>();
  private readonly adjustmentTypeOptionsMap = new Map<string, SelectOption<string>>();

  readonly loadPeriod = (params?: ZnSelectQueryParams) =>
    this.standCatalogs.loadPeriod(
      {
        ...params,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      },
      PERIOD_TYPE_NAME_SILVICULTURE,
    ).pipe(
      tap(response => {
        this.mergeCatalogOptions(this.periodOptionsMap, this.periodOptions, response.items);
      }),
    );
  readonly loadReason = (params?: ZnSelectQueryParams) =>
    this.standCatalogs.loadReason(
      {
        ...params,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      },
      REASON_GROUP_NAME_AREA_ADJUSTMENT,
    ).pipe(
      tap(response => {
        this.mergeCatalogOptions(this.reasonOptionsMap, this.reasonOptions, response.items);
      }),
    );
  readonly loadAdjustmentTypes = (params?: ZnSelectQueryParams) =>
    this.standCatalogs.loadAdjustmentTypes(params).pipe(
      tap(response => {
        this.mergeCatalogOptions(
          this.adjustmentTypeOptionsMap,
          this.adjustmentTypeOptions,
          response.items,
        );
      }),
    );

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });

    const previousStepData =
      context.getStepState<IStepDefineResultingStandsData>(this.previousStepId)?.data;
    const selectedStandStepData =
      context.getStepState<IStepSelectStandToDivideData>(this.firstStepId)?.data;
    const currentStepData =
      context.getStepState<IStepCreateAreaMaintenancesData>(context.activeStepId())?.data;
    const sourceStep2Snapshot = this.buildStep2Snapshot(previousStepData);
    const shouldRestoreCurrentData =
      !!currentStepData && currentStepData.sourceStep2Snapshot === sourceStep2Snapshot;

    this.originalStandId.set(
      previousStepData?.originalStandId ?? currentStepData?.originalStand.standId ?? null,
    );
    this.originalStandName.set(
      previousStepData?.originalStandName ?? currentStepData?.originalStand.standName ?? '',
    );
    this.originalCurrentAreaValue.set(
      selectedStandStepData?.standArea ?? currentStepData?.originalStand.currentArea ?? null,
    );
    this.originalAdjustedArea.set(
      previousStepData?.originalStandArea ?? currentStepData?.originalStand.adjustedArea ?? null,
    );
    this.newStandRows.set(previousStepData?.resultingStands ?? []);

    if (!shouldRestoreCurrentData && currentStepData) {
      context.patchStepState(context.activeStepId(), {
        valid: false,
        completed: false,
        data: undefined,
        summary: [],
      });
    }

    if (shouldRestoreCurrentData) {
      this.seedCatalogOptionsFromMaintenance(currentStepData?.originalStand.maintenance);
      this.seedCatalogOptionsFromMaintenance(currentStepData?.newStands.maintenance);
    } else {
      this.clearCatalogOptions();
    }

    this.originalForm.reset(
      this.buildInitialFormValue(
        shouldRestoreCurrentData ? currentStepData?.originalStand.maintenance : undefined,
      ),
      {
        emitEvent: false,
      },
    );
    this.newStandsForm.reset(
      this.buildInitialFormValue(
        shouldRestoreCurrentData ? currentStepData?.newStands.maintenance : undefined,
      ),
      {
        emitEvent: false,
      },
    );

    if (shouldRestoreCurrentData) {
      queueMicrotask(() => {
        this.originalPeriodSelect()?.writeValue(this.originalForm.getRawValue().period);
        this.originalReasonSelect()?.writeValue(this.originalForm.getRawValue().reason);
        this.originalAdjustmentTypeSelect()?.writeValue(
          this.originalForm.getRawValue().adjustmentType,
        );
        this.newPeriodSelect()?.writeValue(this.newStandsForm.getRawValue().period);
        this.newReasonSelect()?.writeValue(this.newStandsForm.getRawValue().reason);
        this.newAdjustmentTypeSelect()?.writeValue(
          this.newStandsForm.getRawValue().adjustmentType,
        );
      });
    }
  }

  async canLeave(): Promise<boolean> {
    if (!this.validateRequiredForms()) {
      return false;
    }

    const originalStandId = Number(this.originalStandId());
    const selectedPeriodId = Number(this.originalForm.getRawValue().period);

    if (!Number.isFinite(originalStandId) || !Number.isFinite(selectedPeriodId)) {
      return false;
    }

    try {
      await firstValueFrom(
        this.standPartitionPlanningService.ensureAreaMaintenancePeriodAvailable(
          originalStandId,
          selectedPeriodId,
        ),
      );
      return true;
    } catch {
      return false;
    }
  }

  canLeaveSync(): boolean {
    return true;
  }

  collect(context: IWizardContext): IStepCreateAreaMaintenancesData {
    const originalFormValue = this.originalForm.getRawValue();
    const newStandsFormValue = this.newStandsForm.getRawValue();

    const data: IStepCreateAreaMaintenancesData = {
      sourceStep2Snapshot: this.buildStep2Snapshot({
        originalStandId: this.originalStandId(),
        originalStandName: this.originalStandName(),
        originalStandArea: this.originalAdjustedArea(),
        resultingStands: this.newStandRows(),
        rows: [],
        totalArea: 0,
      }),
      originalStand: {
        standId: this.originalStandId(),
        standName: this.originalStandName(),
        currentArea: this.originalCurrentArea(),
        adjustedArea: this.originalAdjustedArea(),
        areaDelta: this.originalAreaDelta(),
        maintenance: this.toAreaMaintenanceFormValue(originalFormValue),
      },
      newStands: {
        standIds: this.newStandRows().map(row => row.id),
        standNames: this.newStandRows().map(row => row.name),
        maintenance: this.toAreaMaintenanceFormValue(newStandsFormValue),
      },
    };

    context.patchStepState(context.activeStepId(), {
      visited: true,
      valid: this.originalForm.valid && (!this.hasNewStands() || this.newStandsForm.valid),
      completed: true,
      data,
    });
    return data;
  }

  fieldInvalid(
    formName: 'original' | 'new',
    field: 'period' | 'reason' | 'adjustmentType' | 'details' | 'isAreaUpdated',
  ): boolean {
    const control = this.resolveForm(formName).get(field);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  formatArea(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '-----';
    }

    return value.toFixed(2).replace('.', ',');
  }

  formatSignedArea(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '-----';
    }

    const absoluteValue = this.formatArea(Math.abs(value));
    if (value > 0) {
      return `+${absoluteValue}`;
    }

    if (value < 0) {
      return `-${absoluteValue}`;
    }

    return absoluteValue;
  }

  private resolveForm(formName: 'original' | 'new') {
    return formName === 'original' ? this.originalForm : this.newStandsForm;
  }

  private buildInitialFormValue(
    value?: IAreaMaintenanceFormValue,
  ): Pick<
    IAreaMaintenanceFormValue,
    'period' | 'reason' | 'adjustmentType' | 'details' | 'isAreaUpdated'
  > {
    return {
      period: value?.period ?? null,
      reason: value?.reason ?? null,
      adjustmentType: value?.adjustmentType ?? null,
      details: value?.details ?? '',
      isAreaUpdated: true,
    };
  }

  private toAreaMaintenanceFormValue(
    rawValue: Partial<IAreaMaintenanceFormValue>,
  ): IAreaMaintenanceFormValue {
    return {
      period: rawValue.period ?? null,
      periodOption: this.resolveSelectedOption(this.periodOptionsMap, rawValue.period),
      reason: rawValue.reason ?? null,
      reasonOption: this.resolveSelectedOption(this.reasonOptionsMap, rawValue.reason),
      adjustmentType: rawValue.adjustmentType ?? null,
      adjustmentTypeOption: this.resolveSelectedOption(
        this.adjustmentTypeOptionsMap,
        rawValue.adjustmentType,
      ),
      details: rawValue.details?.trim() ?? '',
      isAreaUpdated: !!rawValue.isAreaUpdated,
    };
  }

  private seedCatalogOptionsFromMaintenance(value?: IAreaMaintenanceFormValue): void {
    this.seedCatalogOption(this.periodOptionsMap, this.periodOptions, value?.periodOption);
    this.seedCatalogOption(this.reasonOptionsMap, this.reasonOptions, value?.reasonOption);
    this.seedCatalogOption(
      this.adjustmentTypeOptionsMap,
      this.adjustmentTypeOptions,
      value?.adjustmentTypeOption,
    );
  }

  private seedCatalogOption(
    map: Map<string, SelectOption<string>>,
    options: WritableSignal<SelectOption<string>[]>,
    value?: IAreaMaintenanceSelectOption | null,
  ): void {
    if (!value) {
      return;
    }

    map.set(value.key, value);
    options.set(Array.from(map.values()));
  }

  private mergeCatalogOptions(
    map: Map<string, SelectOption<string>>,
    options: WritableSignal<SelectOption<string>[]>,
    items: Array<{ key: string; value: string }>,
  ): void {
    items.forEach(item => {
      map.set(String(item.key), {
        key: String(item.key),
        value: String(item.value),
      });
    });

    options.set(Array.from(map.values()));
  }

  private resolveSelectedOption(
    map: Map<string, SelectOption<string>>,
    key?: string | null,
  ): IAreaMaintenanceSelectOption | undefined {
    if (!key) {
      return undefined;
    }

    const option = map.get(key);
    if (!option) {
      return undefined;
    }

    return {
      key: option.key,
      value: option.value,
    };
  }

  private clearCatalogOptions(): void {
    this.periodOptionsMap.clear();
    this.reasonOptionsMap.clear();
    this.adjustmentTypeOptionsMap.clear();
    this.periodOptions.set([]);
    this.reasonOptions.set([]);
    this.adjustmentTypeOptions.set([]);
  }

  private roundArea(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private validateRequiredForms(): boolean {
    this.originalForm.markAllAsTouched();

    if (this.originalForm.invalid) {
      this.notifyError();
      return false;
    }

    if (this.hasNewStands()) {
      this.newStandsForm.markAllAsTouched();

      if (this.newStandsForm.invalid) {
        this.notifyError();
        return false;
      }
    }

    return true;
  }

  private buildStep2Snapshot(value?: IStepDefineResultingStandsData): string {
    return JSON.stringify({
      originalStandId: value?.originalStandId ?? null,
      originalStandName: value?.originalStandName ?? null,
      originalStandArea: value?.originalStandArea ?? null,
      resultingStands: (value?.resultingStands ?? []).map(row => ({
        id: row.id,
        name: row.name,
        area: this.roundArea(row.area),
        type: row.type,
      })),
    });
  }

  private notifyError(): void {
    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandDivision.StepCreateAreaMaintenances.RequiredFields`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
  }
}
